export const learnEs = {
  meta: {
    title: "Aprender a aprender — Lumen",
    description: "La guía interactiva para entender cómo funcionan la memoria, el cerebro y los mejores métodos de estudio.",
  },
  header: {
    badge: "Guía",
    title: "Aprender a aprender",
    subtitlePre: "No necesitas estudiar más. Necesitas estudiar ",
    subtitleBold: "mejor",
    subtitlePost: ". Así es como funciona realmente tu mente.",
  },
  science: { heading: "La ciencia del estudio" },
  methodsHeading: "Métodos de estudio",
  finalTip: {
    title: "El consejo final",
    body: "No existe el método perfecto para todos. Prueba, mide, adapta. Tu tutor de IA te ayudará a elegir el método adecuado para cada materia.",
  },
  lvl: { easy: "Fácil", media: "Media" },
  badge: { accessible: "Accesible" },
  methodsList: { when: "Cuándo:" },
  dialog: {
    howTo: "Cómo se hace",
    example: "Ejemplo práctico",
    adaptationsTitle: "Variantes accesibles",
    adaptationsFor: "Adaptaciones para",
    noAdaptations: "Sin variante específica: el método base ya es adecuado.",
  },
  adaptMeta: {
    dsa: { label: "TEA", short: "dislexia, disortografía, discalculia" },
    adhd: { label: "TDAH", short: "atención e hiperactividad" },
    bes: { label: "NEE", short: "necesidades educativas especiales" },
    focus: { label: "Concentración", short: "dificultad para mantener el foco" },
  },
  chapters: {
    memory: {
      title: "Cómo funciona la memoria",
      body: "La memoria no es un cajón: es un músculo. Cada vez que RECUPERAS una información — en lugar de releerla — la fijas más profundamente. Esto se llama **Active Recall** (recuperación activa).\n\nLa curva del olvido (Ebbinghaus) muestra que después de 24h recordamos el 30% de lo que hemos leído de forma pasiva. Repasando de forma distribuida (repetición espaciada), la retención sube al 80–90%.",
    },
    brain: {
      title: "Cómo funciona el cerebro cuando estudia",
      body: "El cerebro tiene dos modos: **enfocado** y **difuso** (relajado). Alternarlos — con pausas y sueño — es lo que permite que el aprendizaje se consolide. Estudiar 8h seguidas sin descansos es contraproducente.",
    },
    mistakes: {
      title: "Errores más comunes",
      body: "• **Releer** en lugar de repetir en voz alta.\n• Estudiar de forma **pasiva** con subrayados de colores sin nunca recuperar la información.\n• Dejarlo todo para el último momento (atracón de estudio).\n• No dormir lo suficiente: sin sueño REM, la memoria no consolida.",
    },
    notes: {
      title: "Cómo tomar apuntes",
      body: "El método **Cornell**: divide la página en 3 zonas (apuntes, palabras clave, resumen). Al final de la clase, cierra el libro y completa el resumen de memoria. Aumenta la retención hasta un 34%.",
    },
    anxiety: {
      title: "Cómo gestionar la ansiedad ante un examen",
      body: "La respiración 4-7-8 (inhala 4s, retén 7s, exhala 8s) baja el cortisol. El día antes no estudies temas nuevos: solo **repasa**. La confianza viene de la preparación, no de la ansiedad.",
    },
    forgetting: {
      title: "Cómo evitar olvidar",
      body: "Repasa un tema después de 1 día, 3 días, 7 días, 21 días. Cada recuperación lo hace más fuerte. Es el secreto de los estudiantes de medicina.",
    },
  },
  methods: {
    feynman: {
      d: "Explícalo como si fuera para un niño de 10 años.",
      when: "Cuando necesites entender de verdad un concepto.",
      how: [
        "Elige un concepto y escríbelo en la parte superior de una hoja.",
        "Explícalo en voz alta como si hablaras con un niño de 10 años.",
        "Anota dónde te trabas o usas palabras difíciles.",
        "Vuelve al libro solo en esos puntos y luego explícalo otra vez desde cero.",
      ],
      example: "Concepto: la fotosíntesis.\n\nExplicación para niños: \"Las plantas comen luz. Toman agua de las raíces y aire de las hojas, y con la luz del sol cocinan azúcar para crecer. El aire que respiramos es su 'residuo'.\"\n\nBloqueo típico: no sabes qué tiene que ver la clorofila. → Vuelves al libro, descubres que es la molécula verde que capta la luz, y explicas de nuevo: \"La clorofila es como un panel solar dentro de la hoja.\"",
      tip: "Si no puedes explicarlo de forma sencilla, aún no lo has entendido.",
      adaptations: {
        dsa: [
          "Graba la explicación con la voz en lugar de escribirla — así puedes volver a escucharla.",
          "Usa el tutor de IA de la app para reescribir el concepto con palabras más simples.",
          "Usa una fuente de alta legibilidad y activa el modo TEA en los ajustes.",
        ],
        adhd: [
          "Un concepto a la vez, con un temporizador visible de 10 minutos.",
          "Explica caminando o gesticulando: el movimiento ayuda a mantener el foco.",
          "Recompensa inmediata al terminar (5 min de música, un snack, una pausa).",
        ],
        bes: [
          "Usa dibujos o iconos en lugar de palabras difíciles.",
          "Pide a un amigo o familiar que te escuche: el feedback reduce la ansiedad.",
          "Divide la explicación en 3 mini partes de 2 minutos cada una.",
        ],
        focus: [
          "Elimina distracciones: el teléfono en otra habitación, notificaciones apagadas.",
          "Explica en voz alta como si estuvieras grabando un vídeo: activa más atención.",
        ],
      },
    },
    activeRecall: {
      d: "Cierra el libro. Repite en voz alta.",
      when: "Siempre. Es el método número uno.",
      how: [
        "Lee un párrafo o una página.",
        "Cierra el libro.",
        "Repite en voz alta (o escribe) todo lo que recuerdes.",
        "Vuelve a abrirlo y corrige los errores.",
      ],
      example: "Historia — capítulo sobre la Revolución Francesa.\n\n1) Lee las causas (1789).\n2) Cierra el libro e intenta decir: \"Crisis económica, desigualdad entre los tres estamentos, influencia de la Ilustración, toma de la Bastilla el 14 de julio…\"\n3) Vuelve a abrirlo: olvidaste la hambruna de 1788. Márcalo en rojo.\n4) Inténtalo de nuevo después de 10 minutos — esta vez lo recuerdas.",
      tip: "Releer ≠ estudiar. Si no puedes recuperarlo, no lo sabes.",
      adaptations: {
        dsa: [
          "Repite en voz alta en lugar de escribir: evita la lectura y escritura, que resultan cansadas.",
          "Usa tarjetas de audio: graba la pregunta y escúchala antes de responder.",
          "Pide al tutor de IA que te haga preguntas orales sobre lo que has estudiado.",
        ],
        adhd: [
          "Sesiones cortas de 5-10 minutos, luego cambia de tema para evitar el aburrimiento.",
          "Convierte el repaso en un juego de puntos contigo mismo.",
          "De pie o caminando: el movimiento ayuda a recuperar la información.",
        ],
        bes: [
          "Empieza con 3 preguntas sencillas antes de aumentar la dificultad.",
          "Usa imágenes guía para activar el recuerdo (memoria visual).",
        ],
        focus: [
          "Temporizador corto (10 min) y un objetivo claro: \"recordar 5 puntos\".",
          "Escribe la respuesta enseguida sin releer: eso activa el recuerdo de inmediato.",
        ],
      },
    },
    pomodoro: {
      d: "25 min de foco + 5 min de descanso.",
      when: "Para empezar a estudiar sin procrastinar.",
      how: [
        "Elige UNA tarea precisa (no 'estudiar historia', sino 'leer las páginas 45–52').",
        "Temporizador de 25 min. El teléfono en otra habitación.",
        "5 min de descanso real (levántate, bebe agua, mira por la ventana).",
        "Después de 4 pomodoros: un descanso largo de 15–30 min.",
      ],
      example: "15:00 — Pomodoro 1: leo las páginas 45–52 de historia.\n15:25 — Descanso: camino por la cocina, bebo agua.\n15:30 — Pomodoro 2: cierro el libro y hago Active Recall del capítulo.\n15:55 — Descanso.\n16:00 — Pomodoro 3: resuelvo 5 ejercicios de matemáticas.\n16:25 — Descanso largo, 20 min.",
      tip: "Usa el temporizador de estudio de la app → modo Pomodoro.",
      adaptations: {
        dsa: [
          "Durante el pomodoro alterna 10 min de lectura + 15 min de actividad oral/visual.",
          "Durante el descanso no leas: descansa la vista (mira a lo lejos, luz natural).",
        ],
        adhd: [
          "Reduce a **15 min de foco + 5 min de descanso** — más realista para el TDAH.",
          "Temporizador visible y sonoro. Escribe la tarea en una nota adhesiva delante de ti.",
          "Si te distraes, no te juzgues: nota el pensamiento y vuelve a la tarea.",
        ],
        bes: [
          "Empieza con solo 1 pomodoro al día y auméntalo poco a poco.",
          "Usa un temporizador analógico o un reloj de arena: el tiempo se vuelve concreto.",
        ],
        focus: [
          "Modo avión obligatorio durante los 25 minutos.",
          "Descanso REAL: nada de redes sociales, nada de vídeos. Solo movimiento o agua.",
        ],
      },
    },
    spacedRepetition: {
      d: "Repasa a intervalos crecientes.",
      when: "Para recordar a largo plazo.",
      how: [
        "Estudia hoy (día 0).",
        "Repasa después de 1 día.",
        "Luego después de 3 días, 7 días, 21 días.",
        "Cada vez que falles, vuelve a empezar desde 1 día.",
      ],
      example: "Vocabulario de inglés, 20 palabras nuevas el lunes.\n\n• Mar (día 1): recuerdas las 20 palabras. Fallas 4 → márcalas.\n• Jue (día 3): las recuerdas todas. Las 4 que fallaste vuelven al día 1.\n• Lunes siguiente (día 7): repasa solo las que ya son estables.\n• En 3 semanas: repaso final, 5 minutos.\n\nResultado: 90% de retención después de un mes, frente al 30% con solo relectura.",
      tip: "Las tarjetas de la app ya usan este método (5 cajas Leitner).",
      adaptations: {
        dsa: [
          "Prefiere tarjetas con imágenes o audio, no solo texto.",
          "En la app puedes crear tarjetas de voz: graba la respuesta hablando.",
        ],
        adhd: [
          "Máximo 10 tarjetas por sesión: mazos pequeños evitan que abandones.",
          "Repasa siempre a la misma hora: la rutina ayuda más que la fuerza de voluntad.",
        ],
        bes: [
          "Usa colores distintos por materia: ayuda a encontrar las tarjetas correctas.",
          "Pide ayuda para crear las primeras tarjetas; luego se vuelve algo independiente.",
        ],
        focus: [
          "10 minutos por la mañana justo al despertar: el cerebro está más receptivo.",
          "Cierra todas las demás pestañas/apps antes de empezar el mazo.",
        ],
      },
    },
    cornellNotes: {
      d: "Apuntes en 3 zonas: notas / palabras clave / resumen.",
      when: "Durante las clases.",
      how: [
        "Divide la página en 3 zonas: columna izquierda (palabras clave), columna derecha (notas), franja inferior (resumen).",
        "Durante la clase escribe solo en la columna de notas (derecha).",
        "Justo después, completa las palabras clave a la izquierda.",
        "Por la noche, escribe 2-3 líneas de resumen abajo.",
      ],
      example: "Página de biología.\n\n[Notas derecha] La mitosis tiene 4 fases: profase, metafase, anafase, telofase…\n[Palabras izquierda] mitosis · 4 fases · cromosomas\n[Resumen abajo] La mitosis divide una célula en 2 idénticas. 4 fases. Sirve para el crecimiento y la reparación.",
      tip: "Tapando la columna derecha puedes usar las palabras clave como tarjetas de estudio.",
      adaptations: {
        dsa: [
          "Escribe en letra de imprenta o usa una tableta con una fuente de alta legibilidad.",
          "Graba la clase (con permiso) y completa los apuntes después, con calma.",
          "En la columna izquierda usa símbolos/iconos en lugar de palabras cuando puedas.",
        ],
        adhd: [
          "Concéntrate solo en la columna derecha durante la clase: una cosa a la vez.",
          "Completa las palabras clave y el resumen JUSTO después de la clase, no al día siguiente.",
        ],
        bes: [
          "Plantilla preimpresa con las 3 zonas ya divididas: reduce el esfuerzo organizativo.",
          "Resumen en máximo 2 frases: mejor breve y completo que largo y a medias.",
        ],
        focus: [
          "Bolígrafo siempre en la mano: escribir mantiene la atención aunque no lo copies todo.",
        ],
      },
    },
    blurting: {
      d: "Escribe todo lo que recuerdes, luego compara.",
      when: "Antes de un examen.",
      how: [
        "Elige un tema.",
        "Hoja en blanco. 5-10 min: escribe TODO lo que recuerdes, sin ningún orden particular.",
        "Abre el libro y compara.",
        "Marca en rojo los huecos. Son tu próxima sesión de estudio.",
      ],
      example: "Tema: el sistema circulatorio.\n\nVolcado: \"Corazón, 4 cámaras, aurícula derecha e izquierda, ventrículos, las arterias llevan sangre oxigenada excepto la pulmonar, venas…\"\n\nAbres el libro: olvidaste la válvula tricúspide y el papel de los capilares. Eso es lo que hay que repasar esta noche.",
      tip: "El volcado deja ver los huecos en 10 minutos. Hazlo el día antes de un examen.",
      adaptations: {
        dsa: [
          "Haz el volcado en voz alta: graba un audio en lugar de escribir.",
          "Al comparar con el libro, usa resaltadores de colores para los \"huecos\".",
        ],
        adhd: [
          "Temporizador de 5 minutos: cuando termine, para, aunque no hayas acabado.",
          "Un tema a la vez. No pases al siguiente antes de comparar.",
        ],
        bes: [
          "Puedes usar esquemas o flechas en lugar de frases completas.",
          "El primer volcado siempre está lleno de huecos: eso es NORMAL, no un fracaso.",
        ],
        focus: [
          "Hoja en blanco delante, todo lo demás lejos. Escribe de un tirón sin parar.",
        ],
      },
    },
    mindMapping: {
      d: "Representa los conceptos de forma visual.",
      when: "Para conectar ideas complejas.",
      how: [
        "Escribe el concepto central en el medio de una hoja.",
        "Ramifica con 3-5 ramas principales (subtemas).",
        "Cada rama se divide en subramas con detalles, ejemplos, fechas.",
        "Usa colores distintos para las macroáreas y flechas para las conexiones.",
      ],
      example: "Centro: \"Segunda Guerra Mundial\".\n\nRamas: Causas · Frentes · Personajes · Consecuencias.\n\n• Causas → Tratado de Versalles, crisis de 1929, ascenso del nazismo.\n• Frentes → Europa, Pacífico, África.\n• Personajes → Hitler, Churchill, Roosevelt, Stalin.\n• Consecuencias → ONU, Guerra Fría, descolonización.\n\nFlechas entre 'crisis de 1929' y 'ascenso del nazismo' para mostrar causa-efecto.",
      tip: "En la app puedes generar mapas mentales a partir de tus apuntes en la sección Estudio.",
      adaptations: {
        dsa: [
          "Método IDEAL para TEA: aprovecha la memoria visual y reduce el texto.",
          "Usa colores, iconos y dibujos en lugar de frases largas.",
          "En la app puedes generar el mapa a partir de tus apuntes con un clic.",
        ],
        adhd: [
          "Empieza desde el centro sin planificar demasiado: añade ramas a medida que piensas.",
          "Si te pierdes, detén todo y vuelve al concepto central.",
        ],
        bes: [
          "Mapa pequeño, máximo 3 ramas principales. Mejor simple y claro.",
          "Un color por rama ayuda a memorizar las conexiones.",
        ],
        focus: [
          "Haz el mapa a mano en una hoja grande: el gesto te mantiene despierto.",
          "Haz un mapa a la vez por tema — no mezcles materias.",
        ],
      },
    },
  },
  toast: {
    mustLogin: "Debes iniciar sesión para guardar",
    saved: "Resultado guardado en tu perfil ✨",
  },
  quiz: {
    cta: {
      discoverTitle: "Descubre tu método",
      discoverSubtitle: "7 preguntas rápidas para descubrir qué método de estudio funciona mejor para ti.",
      yourMethod: "Tu método",
      recommendedBadge: "Recomendado",
      savedOn: "guardado el {{date}}",
      thenPrefix: " · luego ",
      andWord: " y ",
      viewCard: "Ver ficha",
      retakeTest: "Repetir el test",
    },
    dialogTitle: "Descubre tu método",
    questionOf: "Pregunta {{n}} de {{total}}",
    resultsSubtitle: "Estos son los métodos que mejor se adaptan a ti",
    noResults: "Sin resultados — inténtalo de nuevo.",
    recommendedBadge: "Recomendado",
    retake: "Repetir el test",
    questions: [
      {
        q: "Cuando estudias algo nuevo, ¿qué te ayuda más a entenderlo?",
        options: [
          "Explicárselo en voz alta a alguien (o a mí mismo)",
          "Ver esquemas, conexiones y dibujos",
          "Repetir en voz alta sin mirar el libro",
        ],
      },
      {
        q: "¿Cuánto te cuesta ponerte a estudiar?",
        options: [
          "Mucho, siempre procrastino",
          "Bastante, después de un rato entro en el ritmo",
          "Poco, empiezo enseguida",
        ],
      },
      {
        q: "¿Cómo tomas apuntes en clase?",
        options: [
          "Escribo prácticamente todo",
          "Solo palabras clave",
          "Escucho y lo reescribo en casa",
        ],
      },
      {
        q: "¿Después de cuánto tiempo sueles olvidar lo que has estudiado?",
        options: [
          "Después de unos días",
          "Después de una semana o dos",
          "Lo recuerdo durante mucho tiempo",
        ],
      },
      {
        q: "¿Cómo sueles prepararte para un examen?",
        options: [
          "Repaso intensivo el día antes",
          "Un poco de repaso cada día durante la semana",
          "Estudio todo la noche antes",
        ],
      },
      {
        q: "¿Qué actividad te ayuda más a fijar la información?",
        options: [
          "Escribir a mano",
          "Hablar, explicar",
          "Dibujar mapas, esquemas, colores",
        ],
      },
      {
        q: "¿Qué tipo de materias te resultan más pesadas?",
        options: [
          "Definiciones, vocabulario, fórmulas",
          "Conceptos abstractos que hay que entender",
          "Temas conectados (historia, literatura)",
        ],
      },
    ],
  },
};
